import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Archive, Download, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import {
  useExportBusiness,
  useInspectBackup,
  useResetBusiness,
  useRestoreFromBackup,
  type UploadedBackupPreview,
} from "@/hooks/use-demo-catalog";
import { useBatchStatus } from "@/hooks/use-import";
import { useCurrentBusiness } from "@/hooks/use-current-business";
import { SettingsPage } from "@/components/v2/settings-page";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DangerAction,
  DangerConfirmDialog,
  DangerZone,
} from "@/components/danger-zone";
import { BatchDiagnosisPanel } from "@/pages/v2/import/components/batch-diagnosis-panel";

const MAX_BACKUP_BYTES = 50 * 1024 * 1024;

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hr ago`;
  return `${Math.floor(diffSec / 86400)} d ago`;
}

export default function V2SettingsBackupResetPage() {
  const {
    data: businessData,
    isLoading: businessLoading,
    isError: businessError,
  } = useCurrentBusiness();
  const business = businessData?.business;
  const exportBackup = useExportBusiness();
  const reset = useResetBusiness();
  const inspect = useInspectBackup();
  const restore = useRestoreFromBackup();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const restoreSectionRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedBackup, setUploadedBackup] = useState<{
    preview: UploadedBackupPreview;
    file: File;
  } | null>(null);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  // ADV-1018: track the active restore batch so we can poll its terminal
  // state and gate the "Restore complete" toast on real completion (not
  // submission ack). batchExpectedCount lets us format the success toast
  // copy using the same number the legacy code surfaced before this fix.
  const [activeRestore, setActiveRestore] = useState<{
    batchId: string;
    expectedCount: number;
    successHandled: boolean;
  } | null>(null);

  // 3 s poll interval, soft 5 min ceiling (research-doc R3): stops polling
  // once we observe `all_terminal` OR after 100 polls (~5 min). Refetch
  // returns false to disable when no batch is active or terminal reached.
  const RESTORE_POLL_INTERVAL_MS = 3_000;
  const RESTORE_POLL_MAX = 100;
  const [pollCount, setPollCount] = useState(0);
  const batchStatus = useBatchStatus(activeRestore?.batchId ?? null, {
    refetchInterval:
      activeRestore &&
      !activeRestore.successHandled &&
      pollCount < RESTORE_POLL_MAX
        ? RESTORE_POLL_INTERVAL_MS
        : false,
  });

  useEffect(() => {
    if (!activeRestore || activeRestore.successHandled) return;
    if (!batchStatus.data) return;
    setPollCount((n) => n + 1);
    const s = batchStatus.data;
    if (!s.all_terminal) return;
    // Terminal state observed — fire the appropriate toast / panel branch.
    if (s.all_completed_no_errors) {
      toast.success("Restore complete", {
        description: `${activeRestore.expectedCount} entity types imported.`,
      });
    } else if (s.any_awaiting_review) {
      toast.message("Restore needs review", {
        description: "One or more imports are waiting for your review.",
      });
    } else if (s.any_errors) {
      toast.error("Restore finished with errors", {
        description: "See the analysis below for the root cause.",
      });
    }
    setActiveRestore((cur) => (cur ? { ...cur, successHandled: true } : null));
  }, [batchStatus.data, activeRestore]);

  const [searchParams, setSearchParams] = useSearchParams();
  const expectedConfirm = business?.slug ?? "";

  // Query-param auto-focus: ?action=download|reset|restore. We run only once on
  // mount, then strip the param so a refresh doesn't re-fire the action.
  const consumedActionParam = useRef(false);
  useEffect(() => {
    if (consumedActionParam.current) return;
    const action = searchParams.get("action");
    if (!action) return;
    consumedActionParam.current = true;

    if (action === "download") {
      void handleDownloadBackup();
    } else if (action === "reset") {
      setResetOpen(true);
    } else if (action === "restore") {
      restoreSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    // Strip the param so the action doesn't re-fire on remount/refresh.
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleDownloadBackup() {
    try {
      await exportBackup.mutateAsync();
      toast.success("Backup downloaded", {
        description: "Saved to your default downloads folder.",
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Download failed";
      toast.error("Backup failed", { description: msg });
    }
  }

  async function handleReset() {
    if (!business) return;
    try {
      const result = await reset.mutateAsync();
      setResetOpen(false);
      toast.success(`${business.name} data reset`, {
        description: `${result.total_rows.toLocaleString()} rows deleted across ${result.tables_touched} tables.`,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Reset failed";
      toast.error("Reset failed", { description: msg });
    }
  }

  async function handleRestore() {
    if (!uploadedBackup) return;
    try {
      const result = await restore.mutateAsync({
        file: uploadedBackup.file,
        resetFirst: true,
      });
      setRestoreConfirmOpen(false);
      setUploadedBackup(null);
      // ADV-1018: do NOT fire toast.success here. The restore endpoint returns
      // immediately with `submitted` status; child jobs run asynchronously and
      // can land in completed | failed | awaiting_review. We now poll the
      // batch status and branch the toast in the effect above.
      setActiveRestore({
        batchId: result.batch_id,
        expectedCount: result.job_count,
        successHandled: false,
      });
      setPollCount(0);
      toast.message("Restore submitted", {
        description: `Importing ${result.job_count} entity types. We'll let you know when it's done.`,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Restore failed";
      toast.error("Restore failed", { description: msg });
    }
  }

  async function handleFile(file: File) {
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please upload a .zip backup file");
      return;
    }
    if (file.size > MAX_BACKUP_BYTES) {
      toast.error("Backup file too large", {
        description: `Maximum size is ${Math.floor(MAX_BACKUP_BYTES / 1024 / 1024)} MB`,
      });
      return;
    }
    try {
      const preview = await inspect.mutateAsync(file);
      setUploadedBackup({ preview, file });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Backup invalid";
      toast.error("Backup invalid", { description: msg });
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  if (businessLoading) {
    return (
      <SettingsPage
        title="Backup & Reset"
        subtitle="Download a backup, restore from a backup, or reset operational data for this business."
      >
        <div className="flex items-center gap-2 typo-body text-[color:var(--color-text-md)]">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading business…
        </div>
      </SettingsPage>
    );
  }

  if (businessError || !business) {
    return (
      <SettingsPage
        title="Backup & Reset"
        subtitle="Download a backup, restore from a backup, or reset operational data for this business."
      >
        <div className="rounded-md bg-destructive/10 px-4 py-3 typo-body text-destructive">
          Couldn&apos;t load business — try signing out and back in.
        </div>
      </SettingsPage>
    );
  }

  return (
    <SettingsPage
      title="Backup & Reset"
      subtitle="Download a backup, restore from a backup, or reset operational data for this business."
    >
      <div className="flex flex-col gap-6">
        {/* Download Backup */}
        <Card>
          <CardHeader>
            <CardTitle className="typo-subtitle">Download Backup</CardTitle>
            <CardDescription className="typo-body text-[color:var(--color-text-lo)]">
              Export the current state of {business.name} as a ZIP file. The
              file can be re-uploaded to restore later.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="outline"
              onClick={handleDownloadBackup}
              disabled={exportBackup.isPending}
            >
              {exportBackup.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Download className="mr-2 h-4 w-4" aria-hidden />
              )}
              Download Backup
            </Button>
          </CardContent>
        </Card>

        {/* Restore from Backup */}
        <div ref={restoreSectionRef}>
          <Card>
            <CardHeader>
              <CardTitle className="typo-subtitle">
                Restore from Backup
              </CardTitle>
              <CardDescription className="typo-body text-[color:var(--color-text-lo)]">
                Upload a backup ZIP to restore {business.name} to that snapshot.
                The current data will be wiped first.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {uploadedBackup ? (
                <UploadedBackupPanel
                  preview={uploadedBackup.preview}
                  onClear={() => setUploadedBackup(null)}
                  onRestoreClick={() => setRestoreConfirmOpen(true)}
                  isPending={restore.isPending}
                />
              ) : (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={onDrop}
                  className={`flex flex-col items-center justify-center gap-3 rounded-md border border-dashed p-6 transition-colors ${
                    isDragging
                      ? "border-[color:var(--color-accent)] bg-[color:var(--color-surface-2)]"
                      : "border-[color:var(--color-border)]"
                  }`}
                >
                  <Upload
                    className="h-6 w-6 text-[color:var(--color-text-lo)]"
                    aria-hidden
                  />
                  <p className="typo-body text-[color:var(--color-text-lo)] text-center">
                    Drag a <code>.zip</code> backup here, or
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={inspect.isPending}
                  >
                    {inspect.isPending ? (
                      <>
                        <Loader2
                          className="mr-2 h-4 w-4 animate-spin"
                          aria-hidden
                        />
                        Reading…
                      </>
                    ) : (
                      "Choose backup file"
                    )}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".zip,application/zip"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void handleFile(file);
                      e.target.value = "";
                    }}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* ADV-1018: inline "What happened?" panel for the restore batch.
              Renders below the upload card once the user has kicked off a
              restore; auto-fires the analysis as soon as the batch reaches
              a terminal state. Stays mounted until the user uploads a new
              file or navigates away. */}
          {activeRestore &&
            batchStatus.data?.all_terminal &&
            (batchStatus.data.any_errors ||
              batchStatus.data.any_awaiting_review) && (
              <div className="mt-4">
                <BatchDiagnosisPanel
                  batchId={activeRestore.batchId}
                  autoFire
                  defaultExpanded
                />
              </div>
            )}
        </div>

        {/* Danger Zone — Reset */}
        <DangerZone description="Permanently delete operational data for this business. Brands, users, custom-field definitions, and integration connections are preserved.">
          <DangerAction
            actionLabel="Reset business data"
            dialogTitle={`Reset ${business.name}?`}
            description={
              <p>
                This will permanently delete all operational data for{" "}
                <strong>{business.name}</strong>. Brands, users, custom-field
                definitions, and integration connections will be preserved. This
                cannot be undone.
              </p>
            }
            confirmSlug={expectedConfirm}
            onConfirm={handleReset}
            isPending={reset.isPending}
            open={resetOpen}
            onOpenChange={setResetOpen}
          />
        </DangerZone>
      </div>

      {/* Restore confirmation — uses the same DangerConfirmDialog primitive */}
      {uploadedBackup && (
        <DangerConfirmDialog
          open={restoreConfirmOpen}
          onOpenChange={setRestoreConfirmOpen}
          title={`Reset and restore ${business.name}?`}
          description={
            <p>
              This will delete all current operational data for{" "}
              <strong>{business.name}</strong>, then import{" "}
              {formatNumber(uploadedBackup.preview.total_records)} records from
              the uploaded backup. This cannot be undone.
            </p>
          }
          confirmSlug={expectedConfirm}
          actionLabel="Reset & restore"
          pendingLabel="Restoring…"
          onConfirm={handleRestore}
          isPending={restore.isPending}
        />
      )}
    </SettingsPage>
  );
}

function UploadedBackupPanel({
  preview,
  onClear,
  onRestoreClick,
  isPending,
}: {
  preview: UploadedBackupPreview;
  onClear: () => void;
  onRestoreClick: () => void;
  isPending: boolean;
}) {
  const topEntities = Object.entries(preview.record_counts)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Archive
            className="h-5 w-5 text-[color:var(--color-text-lo)]"
            aria-hidden
          />
          <div>
            <p className="typo-body font-medium text-[color:var(--color-text-hi)]">
              {preview.display_name}
            </p>
            <p className="typo-label text-[color:var(--color-text-lo)]">
              {preview.business}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-[color:var(--color-text-lo)] hover:text-[color:var(--color-text)]"
          title="Remove uploaded backup"
          aria-label="Remove uploaded backup"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <Badge variant="secondary" className="w-fit">
        Uploaded backup · {formatRelativeTime(preview.exported_at)}
      </Badge>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 typo-body">
        <span className="text-[color:var(--color-text-lo)]">Total records</span>
        <span className="text-right font-medium">
          {formatNumber(preview.total_records)}
        </span>
        <span className="text-[color:var(--color-text-lo)]">Entity types</span>
        <span className="text-right font-medium">
          {Object.values(preview.record_counts).filter((n) => n > 0).length}
        </span>
        <span className="text-[color:var(--color-text-lo)]">
          Schema version
        </span>
        <span className="text-right font-medium">{preview.schema_version}</span>
      </div>

      {topEntities.length > 0 && (
        <div className="space-y-1">
          <span className="typo-label text-[color:var(--color-text-lo)]">
            Top entities
          </span>
          <div className="flex flex-wrap gap-1.5">
            {topEntities.map(([entity, count]) => (
              <Badge key={entity} variant="outline">
                {entity}: {formatNumber(count)}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <Button onClick={onRestoreClick} disabled={isPending} className="w-fit">
        {isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
            Restoring…
          </>
        ) : (
          "Reset & restore"
        )}
      </Button>
    </div>
  );
}
